import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, NgZone, inject } from '@angular/core';
import { ErrorsService, IError, IRemoteError, LocalError, TSeverityCallback, TSeverityCode } from '@partners/errors';
import { NavigationService } from '@partners/navigation';
import { SettingsMap } from '@partners/settings';
import { Console, isFunction } from '@partners/utils';
import { DialogAdapter } from '../../dialogs';
import { GlobalSeverities } from '../model/global-errors.config';
import { TGlobalRemoteError } from '../model/global-errors.types';

@Injectable({
  providedIn: 'root'
})
export class DefaultErrorHandler implements ErrorHandler {
  private _navService = inject(NavigationService);
  private _dialogAdapter = inject(DialogAdapter);
  private _errorsService = inject(ErrorsService);
  private _globalSeverityHandlers = inject(GlobalSeverities);

  // private _shellStore = inject(ShellStore);
  private _ngZone = inject(NgZone);

  constructor() {}

  handleError(error: IError | Error | HttpErrorResponse) {
    let anyError = error as any;

    /**
     * "origin" property on the error object indicates a managed error meaning WE threw  the error
     * from somewhere within our code. if there is not an origin prop on the error it means its uncaught,
     * unexpected and native js error
     */
    const isTypedError = 'origin' in anyError;

    if (!isTypedError) {
      /** unhandled | uncaught js error */
      const formattedMessage = this._errorsService.formatLocalErrorMessage(
        `partners app ** unhandled error **\n${error?.message}\n`
      );
      Console.logError(formattedMessage);
      return;
    }

    /** handled, typed errors */
    switch (anyError.origin) {
      case 'remote': {
        /** remote means originated in the server */
        this.handleRemoteError(anyError as IRemoteError);
        break;
      }

      default: {
        /** local means originated in the browser */
        this.handleLocalError(anyError as Partial<LocalError>);
      }
    }
  }

  private handleLocalError(localError: Partial<LocalError>): void {
    Console.logError(`partners handled error:\n${localError.message}\n\n`);
  }

  /**
   * @description
   * default handling of remote (server originated) errors
   * ---------------------------------------------------------------------------------------------
   * handles errors which originated in the server resulting in an erroneous http status. there are 2
   * default ways to handle these errors:
   * (1) show error dialog (while keeping current feature context, allow retry submitting the http call)
   * (2) navigate to an error page (immediately exiting the feature context, terminate the app process)
   * @param remoteError a remote error object containing all data relevant to the http error
   */
  handleRemoteError(remoteError: IRemoteError): void {
    const { abort, title, message, severity } = remoteError;

    const globalErrorHandler = this.#getGlobalErrorHandler(severity?.code as unknown as TGlobalRemoteError);
    if (isFunction(globalErrorHandler)) {
      globalErrorHandler.call(this, remoteError);
      return;
    }
    if (abort) {
      /** api error handling - scenario (1): navigate to error page to show error details */
      const feature = SettingsMap.get('generic-alert');
      this._ngZone.run(() => {
        this._navService.moveAny(`/${feature.path}/${remoteError.token}`);
      });
      return;
    }

    this._ngZone.run(() => {
      // if (isNotEmpty(this._shellStore.policy$$())) {
      /** api error handling - scenario (2): pop a dialog over current page to show error details */
      this._dialogAdapter.openAlert({ title, message });
      // }

      // this.handleLocalError({ title, message });
    });
  }
  /**
   * @description
   * Some errors like w_0775 (aml error) for example, needs to be handled globally and does not have special
   * handling method for different rest api call.
   * in this method, first we will check if the remote error should be handled globally.
   * if it does, we will extract it's handle method.
   * @param severityCode
   * @returns global error handler
   */
  #getGlobalErrorHandler(severityCode: TSeverityCode): TSeverityCallback | undefined {
    return this._globalSeverityHandlers.handlers.get(severityCode as unknown as TGlobalRemoteError);
  }
}
